// FILE: client/src/pages/Room.js

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import "./Room.css";

import VideoPane from "../components/VideoPane";
import ChatPane from "../components/ChatPane";
import ShoppingPanel from "../components/ShoppingPanel";
import ToastStack from "../components/ToastStack";
import VoiceButton from "../components/VoiceButton";

function extractLatestTheme(chatMessages) {
  const msgs = Array.isArray(chatMessages) ? chatMessages : [];
  for (let i = msgs.length - 1; i >= 0; i--) {
    const text = (msgs[i]?.text || "").trim();
    if (text.toLowerCase().startsWith("🎨 theme:")) {
      const raw = text.split(":").slice(1).join(":").trim().toLowerCase();
      if (raw) return raw;
    }
  }
  return null;
}

function extractLatestShoppingLink(chatMessages) {
  const msgs = Array.isArray(chatMessages) ? chatMessages : [];
  for (let i = msgs.length - 1; i >= 0; i--) {
    const text = msgs[i]?.text || "";
    if (text.startsWith("🛒 ")) {
      const match = text.match(/https?:\/\/[^\s)]+/i);
      if (match?.[0]) return match[0];
    }
  }
  return null;
}

function getMemberId(member) {
  if (!member) return "";
  if (typeof member === "string") return member;
  return String(
    member.id ||
    member.socketId ||
    member.userId ||
    member.memberId ||
    member.sid ||
    ""
  );
}

function getMemberName(member) {
  if (!member) return "Unknown";
  if (typeof member === "string") return member;
  return String(
    member.name ||
    member.username ||
    member.displayName ||
    member.label ||
    member.id ||
    "Unknown"
  );
}

function normalizeMembers(list) {
  const raw = Array.isArray(list) ? list : [];
  const seen = new Set();
  const out = [];

  for (const member of raw) {
    const id = getMemberId(member);
    const name = getMemberName(member);

    const key = id || `name:${name}`;
    if (seen.has(key)) continue;
    seen.add(key);

    out.push({ id, name, raw: member });
  }

  return out;
}

export default function Room({
  socket,
  room,
  isHost,
  hostId,
  hostName,
  shareLink,
  copyRoomLink,
  viewerTapToStart,
  hostSetVideo,

  video,
  mp4Ref,
  setMp4Ready,
  onMp4Play,
  onMp4Pause,
  hostSeek,

  chatMessages,
  chatInput,
  setChatInput,
  sendChat,
  chatEndRef,

  mutedUsers,
  muteUser,
  unmuteUser,

  isSelfMuted,
  requestUnmute,
  unmuteStatusText,

  roomMembers,
  mutedRoster,
  lastHostRequest,
  setLastHostRequest,
  grantHost,
  passHost,
  requestHost,
  requestSent,

  unmuteRequests,
  approveUnmuteRequest,
  ignoreUnmuteRequest,

  kickUser,
  showHostDrawer,
  setShowHostDrawer,
}) {
  const latestThemeFromChat = useMemo(
    () => extractLatestTheme(chatMessages),
    [chatMessages]
  );

  const latestShoppingLink = useMemo(
    () => extractLatestShoppingLink(chatMessages),
    [chatMessages]
  );

  const themePrevRef = useRef(null);
  const [showUsersPanel, setShowUsersPanel] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);

  useEffect(() => {
    if (!isHost) return;

    const done = localStorage.getItem("roomTutorialDone");
    if (done) return;

    setTutorialStep(4); // start at input
  }, [isHost]);

  useEffect(() => {
    if (tutorialStep === 7) {
      localStorage.setItem("roomTutorialDone", "true");
    }
  }, [tutorialStep]);

  const helperHintText = useMemo(() => {
    try {
      if (typeof window === "undefined") return "";
      const ua = navigator?.userAgent || "";
      const isMobileUA = /Mobi|Android|iPhone|iPod/i.test(ua);
      const isNarrow = window.matchMedia
        ? window.matchMedia("(max-width: 768px)").matches
        : false;

      const isPhone = isMobileUA && isNarrow;
      return isPhone ? "For better video + chat experience, use landscape" : "";
    } catch {
      return "";
    }
  }, []);

  const [toasts, setToasts] = useState([]);

  const pushToast = useCallback((text) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((prev) => [...prev, { id, text }]);

    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 1600);
  }, []);

  const [micToasts, setMicToasts] = useState([]);
  const pushMicToast = useCallback((title, text, ms = 2600) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setMicToasts([{ id, title, text }]);

    window.setTimeout(() => {
      setMicToasts((prev) => prev.filter((t) => t.id !== id));
    }, ms);
  }, []);

  const VOICE_BTN_RIGHT = 18;
  const VOICE_BTN_BOTTOM = 120;
  const VOICE_BTN_SIZE = 56;
  const VOICE_TOAST_GAP = 12;

  const micToastAnchor = useMemo(
    () => ({
      right: VOICE_BTN_RIGHT,
      bottom: VOICE_BTN_BOTTOM + VOICE_BTN_SIZE + VOICE_TOAST_GAP,
    }),
    []
  );

  function fmtTime(seconds) {
    const s = Math.max(0, Number(seconds || 0));
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${m}:${String(r).padStart(2, "0")}`;
  }

  const THEMES = useMemo(
    () => ({
      midnight: {
        "--bg": "#0b1220",
        "--panel": "rgba(18, 20, 34, 0.82)",
        "--panel-2": "rgba(18, 20, 34, 0.9)",
        "--accent": "#60a5fa",
        "--accent-2": "#a78bfa",
        "--accent-glow": "rgba(96, 165, 250, 0.46)",
        "--theme-wash": "rgba(96, 165, 250, 0.10)",
        "--theme-wash-strong": "rgba(96, 165, 250, 0.22)",
      },
      purple: {
        "--bg": "#0b0618",
        "--panel": "rgba(24, 12, 44, 0.84)",
        "--panel-2": "rgba(24, 12, 44, 0.92)",
        "--accent": "#9146ff",
        "--accent-2": "#b794ff",
        "--accent-glow": "rgba(145, 70, 255, 0.50)",
        "--theme-wash": "rgba(145, 70, 255, 0.12)",
        "--theme-wash-strong": "rgba(145, 70, 255, 0.24)",
      },
      red: {
        "--bg": "#14060a",
        "--panel": "rgba(44, 12, 18, 0.84)",
        "--panel-2": "rgba(44, 12, 18, 0.92)",
        "--accent": "#ef4444",
        "--accent-2": "#fb7185",
        "--accent-glow": "rgba(239, 68, 68, 0.50)",
        "--theme-wash": "rgba(239, 68, 68, 0.13)",
        "--theme-wash-strong": "rgba(239, 68, 68, 0.26)",
      },
      blue: {
        "--bg": "#071426",
        "--panel": "rgba(10, 25, 46, 0.84)",
        "--panel-2": "rgba(10, 25, 46, 0.92)",
        "--accent": "#3b82f6",
        "--accent-2": "#22d3ee",
        "--accent-glow": "rgba(59, 130, 246, 0.50)",
        "--theme-wash": "rgba(59, 130, 246, 0.13)",
        "--theme-wash-strong": "rgba(59, 130, 246, 0.26)",
      },
      green: {
        "--bg": "#061410",
        "--panel": "rgba(8, 38, 28, 0.84)",
        "--panel-2": "rgba(8, 38, 28, 0.92)",
        "--accent": "#22c55e",
        "--accent-2": "#34d399",
        "--accent-glow": "rgba(34, 197, 94, 0.50)",
        "--theme-wash": "rgba(34, 197, 94, 0.13)",
        "--theme-wash-strong": "rgba(34, 197, 94, 0.26)",
      },
      gold: {
        "--bg": "#120f06",
        "--panel": "rgba(46, 34, 10, 0.84)",
        "--panel-2": "rgba(46, 34, 10, 0.92)",
        "--accent": "#f59e0b",
        "--accent-2": "#fde047",
        "--accent-glow": "rgba(245, 158, 11, 0.50)",
        "--theme-wash": "rgba(245, 158, 11, 0.13)",
        "--theme-wash-strong": "rgba(245, 158, 11, 0.26)",
      },
    }),
    []
  );

  useEffect(() => {
    if (!socket) return;
    if (isHost) return;

    const handler = (eventName, ...args) => {
      const e = String(eventName || "").toLowerCase();

      if (
        e.includes("chat") ||
        e.includes("typing") ||
        e.includes("ping") ||
        e.includes("pong") ||
        e.includes("join") ||
        e.includes("leave")
      ) {
        return;
      }

      if (e.includes("pause")) {
        pushToast("Host paused");
        return;
      }
      if (e.includes("play") || e.includes("resume")) {
        pushToast("Host resumed");
        return;
      }

      if (e.includes("seek")) {
        const maybeTime =
          typeof args?.[0] === "number"
            ? args[0]
            : typeof args?.[0]?.time === "number"
              ? args[0].time
              : typeof args?.[0]?.t === "number"
                ? args[0].t
                : null;

        if (maybeTime != null) {
          pushToast(`Host jumped to ${fmtTime(maybeTime)}`);
        } else {
          pushToast("Host seeked");
        }
        return;
      }

      if (e.includes("set") || e.includes("link") || e.includes("url")) {
        pushToast("Host changed video");
      }
    };

    socket.onAny(handler);
    return () => socket.offAny(handler);
  }, [socket, isHost, pushToast]);

  const keysToTouch = useMemo(() => Object.keys(THEMES.midnight), [THEMES]);

  useEffect(() => {
    const root = document.documentElement;

    if (!themePrevRef.current) {
      const prev = {};
      keysToTouch.forEach((k) => {
        prev[k] = root.style.getPropertyValue(k);
      });
      themePrevRef.current = prev;
    }

    const prev = themePrevRef.current;

    const applyThemeVars = (themeName) => {
      const theme = THEMES[themeName];
      if (!theme) return false;
      Object.entries(theme).forEach(([k, v]) => root.style.setProperty(k, v));
      return true;
    };

    const revertThemeVars = () => {
      keysToTouch.forEach((k) => {
        if (prev[k]) root.style.setProperty(k, prev[k]);
        else root.style.removeProperty(k);
      });
    };

    const params = new URLSearchParams(window.location.search);
    const urlTheme = (params.get("theme") || "").trim().toLowerCase();
    if (urlTheme) {
      const ok = applyThemeVars(urlTheme);
      if (!ok) revertThemeVars();
      return;
    }

    const videoTheme = (video?.theme || "").trim().toLowerCase();
    if (videoTheme) {
      const ok = applyThemeVars(videoTheme);
      if (!ok) revertThemeVars();
      return;
    }

    if (latestThemeFromChat) {
      const ok = applyThemeVars(latestThemeFromChat);
      if (!ok) revertThemeVars();
      return;
    }

    revertThemeVars();
  }, [THEMES, keysToTouch, latestThemeFromChat, video]);

  const lastShoppingToastTsRef = useRef(0);

  const lastOpenTogetherLinkRef = useRef("");
  const [openTogetherGlow, setOpenTogetherGlow] = useState(false);

  useEffect(() => {
    const msgs = Array.isArray(chatMessages) ? chatMessages : [];
    if (!msgs.length) return;

    const last = msgs[msgs.length - 1];
    const text = String(last?.text || "").trim();

    if (!text.startsWith("🛒")) return;

    const ts = Number(last?.ts || 0) || Date.now();
    if (ts <= lastShoppingToastTsRef.current) return;
    lastShoppingToastTsRef.current = ts;

    pushMicToast(
      "Stay together",
      "Turn mic on to keep talking while you shop",
      2800
    );
  }, [chatMessages, pushMicToast]);

  useEffect(() => {
    if (!latestShoppingLink) {
      lastOpenTogetherLinkRef.current = "";
      setOpenTogetherGlow(false);
      return;
    }

    if (latestShoppingLink !== lastOpenTogetherLinkRef.current) {
      lastOpenTogetherLinkRef.current = latestShoppingLink;
      setOpenTogetherGlow(true);
    }
  }, [latestShoppingLink]);

  const [, setVoiceConnected] = useState(false);
  const [micOn, setMicOn] = useState(false);

  const [remoteMicOn, setRemoteMicOn] = useState({});
  const anyRemoteMicOn = useMemo(() => {
    return Object.values(remoteMicOn).some(Boolean);
  }, [remoteMicOn]);

  const localStreamRef = useRef(null);
  const peersRef = useRef({});
  const joinedVoiceRef = useRef(false);

  const rtcConfig = useMemo(
    () => ({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    }),
    []
  );

  const stopAllVoice = useCallback(() => {
    try {
      Object.values(peersRef.current).forEach(({ pc, audioEl }) => {
        try {
          if (audioEl) {
            audioEl.srcObject = null;
            audioEl.remove();
          }
        } catch { }
        try {
          pc?.close();
        } catch { }
      });
      peersRef.current = {};

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => {
          try {
            t.stop();
          } catch { }
        });
        localStreamRef.current = null;
      }
    } catch { }
    joinedVoiceRef.current = false;
    setVoiceConnected(false);
    setMicOn(false);
  }, []);

  const ensureLocalAudio = useCallback(async () => {
    if (localStreamRef.current) return localStreamRef.current;

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });

    localStreamRef.current = stream;
    return stream;
  }, []);

  const attachLocalTracks = useCallback((pc, stream) => {
    stream.getTracks().forEach((track) => {
      pc.addTrack(track, stream);
    });
  }, []);

  const ensurePeer = useCallback(
    async (peerId) => {
      if (peersRef.current[peerId]?.pc) return peersRef.current[peerId].pc;

      const pc = new RTCPeerConnection(rtcConfig);

      pc.onicecandidate = (e) => {
        if (!e.candidate) return;
        socket?.emit("voice:signal", {
          room,
          to: peerId,
          data: { type: "ice", candidate: e.candidate },
        });
      };

      pc.ontrack = (e) => {
        const stream = e.streams?.[0];
        if (!stream) return;

        let entry = peersRef.current[peerId];
        if (!entry) entry = { pc, audioEl: null };

        if (!entry.audioEl) {
          const audio = document.createElement("audio");
          audio.autoplay = true;
          audio.playsInline = true;
          audio.style.display = "none";
          document.body.appendChild(audio);
          entry.audioEl = audio;
        }

        entry.audioEl.srcObject = stream;
        peersRef.current[peerId] = entry;
      };

      peersRef.current[peerId] = { pc, audioEl: null };
      return pc;
    },
    [rtcConfig, room, socket]
  );

  const sendOfferTo = useCallback(
    async (peerId) => {
      const stream = await ensureLocalAudio();
      const pc = await ensurePeer(peerId);

      const senders = pc.getSenders();
      const hasAudioSender = senders.some(
        (s) => s.track && s.track.kind === "audio"
      );
      if (!hasAudioSender) attachLocalTracks(pc, stream);

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      socket?.emit("voice:signal", {
        room,
        to: peerId,
        data: { type: "offer", sdp: pc.localDescription },
      });
    },
    [attachLocalTracks, ensureLocalAudio, ensurePeer, room, socket]
  );

  const handleSignal = useCallback(
    async ({ from, data }) => {
      if (!from || !data) return;

      const pc = await ensurePeer(from);

      if (data.type === "offer") {
        const stream = await ensureLocalAudio();
        const senders = pc.getSenders();
        const hasAudioSender = senders.some(
          (s) => s.track && s.track.kind === "audio"
        );
        if (!hasAudioSender) attachLocalTracks(pc, stream);

        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        socket?.emit("voice:signal", {
          room,
          to: from,
          data: { type: "answer", sdp: pc.localDescription },
        });
        return;
      }

      if (data.type === "answer") {
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
        return;
      }

      if (data.type === "ice" && data.candidate) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
        } catch { }
      }
    },
    [attachLocalTracks, ensureLocalAudio, ensurePeer, room, socket]
  );

  const joinVoice = useCallback(async () => {
    if (!socket || !room) return;
    if (joinedVoiceRef.current) return;

    await ensureLocalAudio();

    socket.emit("voice:join", { room });

    joinedVoiceRef.current = true;
    setVoiceConnected(true);
  }, [ensureLocalAudio, room, socket]);

  const setMuted = useCallback((muted) => {
    if (!localStreamRef.current) return;
    localStreamRef.current.getTracks().forEach((t) => {
      t.enabled = !muted;
    });
  }, []);

  const toggleMic = useCallback(async () => {
    if (!joinedVoiceRef.current) {
      try {
        await joinVoice();
        setMuted(false);
        setMicOn(true);

        try {
          socket?.emit("mic:on", { room });
        } catch { }
      } catch (e) {
        console.error("Voice start failed:", e);
        pushToast("Mic permission blocked");
      }
      return;
    }

    const next = !micOn;
    setMuted(!next);
    setMicOn(next);

    try {
      socket?.emit(next ? "mic:on" : "mic:off", { room });
    } catch { }
  }, [joinVoice, micOn, pushToast, room, setMuted, socket]);

  useEffect(() => {
    if (!socket) return;

    const onMicUserOn = ({ id, name }) => {
      if (!id) return;
      if (id === socket.id) return;

      setRemoteMicOn((prev) => ({ ...prev, [id]: true }));

      const who = String(name || "Someone").trim() || "Someone";
      pushMicToast(`${who} is ready to talk`, "Tap the mic to join", 2800);
    };

    const onMicUserOff = ({ id, name }) => {
      if (!id) return;
      if (id === socket.id) return;

      setRemoteMicOn((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });

      const who = String(name || "Someone").trim() || "Someone";
      pushMicToast(`${who} turned mic off`, "Voice is paused", 2400);
    };

    socket.on("mic:user-on", onMicUserOn);
    socket.on("mic:user-off", onMicUserOff);

    return () => {
      socket.off("mic:user-on", onMicUserOn);
      socket.off("mic:user-off", onMicUserOff);
    };
  }, [pushMicToast, socket]);

  useEffect(() => {
    if (!socket) return;

    const onPeers = async ({ peers }) => {
      const list = Array.isArray(peers) ? peers : [];

      for (const peerId of list) {
        try {
          await sendOfferTo(peerId);
        } catch (e) {
          console.warn("offer error", e);
        }
      }
    };

    const onSignal = async (payload) => {
      try {
        await handleSignal(payload);
      } catch (e) {
        console.warn("signal error", e);
      }
    };

    const onLeave = ({ id }) => {
      const entry = peersRef.current[id];
      if (!entry) return;

      try {
        entry.audioEl?.remove();
      } catch { }
      try {
        entry.pc?.close();
      } catch { }

      delete peersRef.current[id];

      setRemoteMicOn((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    };

    socket.on("voice:peers", onPeers);
    socket.on("voice:signal", onSignal);
    socket.on("voice:leave", onLeave);

    return () => {
      socket.off("voice:peers", onPeers);
      socket.off("voice:signal", onSignal);
      socket.off("voice:leave", onLeave);
    };
  }, [handleSignal, sendOfferTo, socket]);

  useEffect(() => {
    return () => {
      try {
        if (socket && room && joinedVoiceRef.current) {
          try {
            socket.emit("mic:off", { room });
          } catch { }
          socket.emit("voice:leave", { room });
        }
      } catch { }
      stopAllVoice();
    };
  }, [room, socket, stopAllVoice]);

  const effectiveVoiceConnected = useMemo(() => {
    return !!(micOn && anyRemoteMicOn);
  }, [anyRemoteMicOn, micOn]);

  const openTogether = useCallback(async () => {
    if (!latestShoppingLink) return;
    setOpenTogetherGlow(false);
    if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
      try {
        await Browser.open({ url: latestShoppingLink });
        return;
      } catch { }
    }

    window.open(latestShoppingLink, "_blank", "noopener,noreferrer");
  }, [latestShoppingLink]);

  const normalizedMembers = useMemo(() => {
    return normalizeMembers(roomMembers);
  }, [roomMembers]);

  const normalizedMutedRoster = useMemo(() => {
    return normalizeMembers(mutedRoster);
  }, [mutedRoster]);

  const mutedLookup = useMemo(() => {
    return mutedUsers || {};
  }, [mutedUsers]);

  const visibleMutedMembers = useMemo(() => {
    const fromRoom = normalizedMembers.filter(
      (member) => mutedLookup[member.id]
    );

    const byId = new Set(fromRoom.map((m) => m.id || `name:${m.name}`));
    const extraMuted = normalizedMutedRoster.filter((member) => {
      const key = member.id || `name:${member.name}`;
      return !byId.has(key);
    });

    return [...fromRoom, ...extraMuted];
  }, [normalizedMembers, normalizedMutedRoster, mutedLookup]);

  return (
    <div className="roomPageClean">
      <ToastStack toasts={toasts} />
      <ToastStack toasts={micToasts} anchor={micToastAnchor} />

      <VoiceButton
        micOn={micOn}
        voiceConnected={effectiveVoiceConnected}
        disabled={!socket}
        onToggle={toggleMic}
      />

      <div className="roomBodyGrid">
        <div className="roomMainColumn">
          {video ? (
            <div className="roomVideoSection">
              <div className="videoShellClean">
                <VideoPane
                  isHost={isHost}
                  video={video}
                  mp4Ref={mp4Ref}
                  setMp4Ready={setMp4Ready}
                  onMp4Play={onMp4Play}
                  onMp4Pause={onMp4Pause}
                  hostSeek={hostSeek}
                  viewerTapToStart={viewerTapToStart}
                />
              </div>
            </div>
          ) : null}

          <div className="roomInfoRow" />

          <div className="roomLinkInput">
            <ShoppingPanel
              socket={socket}
              room={room}
              isHost={isHost}
              chatMessages={chatMessages}
              showInput={isHost}
              onSubmitLink={hostSetVideo}
              tutorialStep={tutorialStep}
              onTutorialLinkInput={() => {
                if (tutorialStep === 4) setTutorialStep(5);
              }}
              onTutorialLinkSet={() => {
                if (tutorialStep === 5) setTutorialStep(6);
              }}
            />
          </div>
        </div>

        <div className="roomChatSection">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
              gap: 10,
            }}
          >
            <div style={{ fontWeight: 700, flex: "0 0 auto" }}>Chat</div>

            <div
              className="roomTopButtons"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flex: 1,
                justifyContent: "flex-end",
                minWidth: 0,
              }}
            >
              {latestShoppingLink ? (
                <button
                  className={`btn btn-primary ${openTogetherGlow ? "openTogetherGlow" : ""}`}
                  onClick={openTogether}
                  style={{ flex: 1, whiteSpace: "nowrap" }}
                >
                  Open Together
                </button>
              ) : null}

              <div
                className={`tutorial-ring tutorial-ring-inline ${tutorialStep === 6 ? "is-active" : ""
                  }`}
                style={{ flex: 1 }}
              >
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    copyRoomLink();
                    setTutorialStep(7); // finish tutorial
                  }}
                  style={{ width: "100%", whiteSpace: "nowrap" }}
                >
                  Invite
                </button>
              </div>

              {isHost && (
                <button
                  className="btn btn-primary"
                  onClick={() => setShowUsersPanel((prev) => !prev)}
                  style={{ flex: 1, whiteSpace: "nowrap" }}
                >
                  Users
                </button>
              )}
            </div>
          </div>

          {isHost && showUsersPanel && (
            <div
              style={{
                marginBottom: 10,
                padding: 12,
                borderRadius: 16,
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                boxShadow: "0 12px 30px rgba(0, 0, 0, 0.18)",
                backdropFilter: "blur(10px)",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  marginBottom: 10,
                  color: "var(--text)",
                }}
              >
                People in Room
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {normalizedMembers.length ? (
                  normalizedMembers.map((member) => {
                    const memberId = member.id;
                    const memberName = member.name;
                    const isCurrentHost = !!hostId && memberId === hostId;
                    const isMe = !!socket?.id && memberId === socket.id;
                    const isMuted = !!mutedLookup[memberId];
                    const canModerate = !!memberId && !isCurrentHost && !isMe;

                    return (
                      <div
                        key={memberId || memberName}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 10,
                          padding: "10px 12px",
                          borderRadius: 14,
                          background: "rgba(255, 255, 255, 0.04)",
                          border: "1px solid rgba(255, 255, 255, 0.06)",
                        }}
                      >
                        <div
                          style={{
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              color: "var(--text)",
                              fontWeight: 700,
                              wordBreak: "break-word",
                            }}
                          >
                            {memberName}
                          </span>

                          {isCurrentHost && (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "3px 8px",
                                borderRadius: 999,
                                background: "#9146ff",
                                color: "#fff",
                                fontSize: 11,
                                fontWeight: 800,
                                letterSpacing: "0.04em",
                              }}
                            >
                              HOST
                            </span>
                          )}

                          {isMe && (
                            <span
                              style={{
                                opacity: 0.8,
                                fontSize: 12,
                              }}
                            >
                              (You)
                            </span>
                          )}
                        </div>

                        {canModerate ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              flexWrap: "wrap",
                              justifyContent: "flex-end",
                            }}
                          >
                            {isMuted ? (
                              <button
                                className="btn btn-small"
                                onClick={() => unmuteUser(memberId)}
                              >
                                Unmute
                              </button>
                            ) : (
                              <button
                                className="btn btn-small"
                                onClick={() => muteUser(memberId, 0)}
                              >
                                Mute
                              </button>
                            )}

                            <button
                              className="btn btn-small"
                              onClick={() => grantHost(memberId)}
                            >
                              Pass Host
                            </button>

                            <button
                              className="btn btn-small"
                              onClick={() => kickUser(memberId)}
                            >
                              Kick
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                ) : (
                  <div style={{ opacity: 0.8 }}>No one else is here yet.</div>
                )}
              </div>

              <div
                style={{
                  fontWeight: 800,
                  marginTop: 14,
                  marginBottom: 10,
                  color: "var(--text)",
                }}
              >
                Muted Users
              </div>

              {visibleMutedMembers.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {visibleMutedMembers.map((member) => (
                    <div
                      key={`muted-${member.id || member.name}`}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 12px",
                        borderRadius: 14,
                        background: "rgba(255, 255, 255, 0.04)",
                        border: "1px solid rgba(255, 255, 255, 0.06)",
                      }}
                    >
                      <span
                        style={{
                          color: "var(--text)",
                          fontWeight: 700,
                          wordBreak: "break-word",
                        }}
                      >
                        {member.name}
                      </span>

                      {member.id ? (
                        <button
                          className="btn btn-small"
                          onClick={() => unmuteUser(member.id)}
                        >
                          Unmute
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ opacity: 0.8 }}>No muted users.</div>
              )}
            </div>
          )}

          <div className="chatSectionCard">
            <ChatPane
              socket={socket}
              room={room}
              isHost={isHost}
              hostId={hostId}
              mutedUsers={mutedUsers}
              muteUser={muteUser}
              unmuteUser={unmuteUser}
              chatMessages={chatMessages}
              chatInput={chatInput}
              setChatInput={setChatInput}
              sendChat={sendChat}
              chatEndRef={chatEndRef}
              isSelfMuted={isSelfMuted}
              requestUnmute={requestUnmute}
              unmuteStatusText={unmuteStatusText}
              helperHintText={helperHintText}
            />
          </div>
        </div>
      </div>

      <div
        className="roomBottomMeta"
        style={{
          marginTop: 10,
          display: "flex",
          alignItems: "center",
          gap: 10,
          justifyContent: "flex-start",
        }}
      >
        <span className="roomName">Room: {room}</span>

        {isHost && (
          <span
            style={{
              padding: "4px 10px",
              borderRadius: "999px",
              background: "#111",
              border: "1px solid rgba(255,255,255,0.2)",
              fontSize: "11px",
              fontWeight: 700,
            }}
          >
            HOST
          </span>
        )}
      </div>
    </div>
  );
}